import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`,
      { email, password, name, role: 'investor' },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const processGoogleAuth = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';
  const isInvestor = () => user?.role === 'investor';
  // Both admin and investor have full CRUD access to their own data
  const canEdit = () => user?.role === 'admin' || user?.role === 'investor';
  const canDelete = () => user?.role === 'admin' || user?.role === 'investor';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      processGoogleAuth,
      checkAuth,
      isAdmin,
      isInvestor,
      canEdit,
      canDelete
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
